import { createAction, props } from '@ngrx/store';
import {
  BatchSummary,
  BatchProgress,
  ImportConnectionsResult,
  MatchQueueItem,
  UploadExcelParams,
  ReviewResponse,
  AutoMatchedResponse,
  BrandOrg,
} from '../../models/market-research.model';

// Mode & Step Navigation
export const setMode = createAction(
  '[Market Research] Set Mode',
  props<{ mode: 'upload' | 'load' }>()
);

export const goToStep = createAction(
  '[Market Research] Go To Step',
  props<{ step: 1 | 2 | 3 | 4 }>()
);

export const resetFlow = createAction('[Market Research] Reset Flow');

// Brand & File Selection
export const selectBrand = createAction(
  '[Market Research] Select Brand',
  props<{ brand: BrandOrg | null }>()
);

export const searchBrandOrgs = createAction(
  '[Market Research] Search Brand Orgs',
  props<{ orgName: string }>()
);

export const searchBrandOrgsSuccess = createAction(
  '[Market Research] Search Brand Orgs Success',
  props<{ brands: BrandOrg[] }>()
);

export const searchBrandOrgsFail = createAction(
  '[Market Research] Search Brand Orgs Fail',
  props<{ error: string }>()
);

// Separate brand-search state for the "Load existing data" resume tab, so
// searching there doesn't overwrite (or get overwritten by) the "New upload"
// brand search — they're independent fields sharing the same lookup API.
export const searchResumeBrandOrgs = createAction(
  '[Market Research] Search Resume Brand Orgs',
  props<{ orgName: string }>()
);

export const searchResumeBrandOrgsSuccess = createAction(
  '[Market Research] Search Resume Brand Orgs Success',
  props<{ brands: BrandOrg[] }>()
);

export const searchResumeBrandOrgsFail = createAction(
  '[Market Research] Search Resume Brand Orgs Fail',
  props<{ error: string }>()
);

export const selectFile = createAction(
  '[Market Research] Select File',
  props<{ file: File | null }>()
);

// Template Download
export const downloadTemplate = createAction('[Market Research] Download Template');

export const downloadTemplateSuccess = createAction(
  '[Market Research] Download Template Success',
  props<{ blob: Blob }>()
);

export const downloadTemplateFail = createAction(
  '[Market Research] Download Template Fail',
  props<{ error: string }>()
);

// Upload File (Trio Pattern)
export const uploadFile = createAction(
  '[Market Research] Upload File',
  props<{ file: File; params: UploadExcelParams }>()
);

export const uploadFileSuccess = createAction(
  '[Market Research] Upload File Success',
  props<{ batchId: string }>()
);

export const uploadFileFail = createAction(
  '[Market Research] Upload File Fail',
  props<{ error: string }>()
);

// Polling Progress
export const startPolling = createAction(
  '[Market Research] Start Polling',
  props<{ batchId: string }>()
);

export const pollProgress = createAction(
  '[Market Research] Poll Progress',
  props<{ batchId: string }>()
);

export const pollProgressTick = createAction(
  '[Market Research] Poll Progress Tick',
  props<{ progress: BatchProgress }>()
);

export const pollProgressComplete = createAction(
  '[Market Research] Poll Progress Complete',
  props<{ batchId: string; batchStatus: string }>()
);

export const pollProgressFail = createAction(
  '[Market Research] Poll Progress Fail',
  props<{ error: string }>()
);

/**
 * Resuming a batch can't trust BatchSummary.needsReview from the /batches
 * list — that's a one-time snapshot written when matching finished and is
 * never updated as rows get confirmed/rejected afterwards. This fetches the
 * live per-item counts (same as /progress) either to decide the next step
 * (the "Save & continue" case: skip to export once nothing's left pending)
 * or, when `alwaysGoToResolve` is set (resuming from the batch list), to
 * always land on step 3 — reviewing every row again doesn't mean the user
 * wants to skip straight past Resolve to the Import decision unprompted.
 */
export const checkResumeProgress = createAction(
  '[Market Research] Check Resume Progress',
  props<{ batchId: string; alwaysGoToResolve?: boolean }>()
);

// Load Review Items
export const loadReviewItems = createAction(
  '[Market Research] Load Review Items',
  props<{ batchId: string }>()
);

export const loadReviewItemsSuccess = createAction(
  '[Market Research] Load Review Items Success',
  props<{ response: ReviewResponse }>()
);

export const loadReviewItemsFail = createAction(
  '[Market Research] Load Review Items Fail',
  props<{ error: string }>()
);

// Load Auto-Matched Items
export const loadAutoMatchedItems = createAction(
  '[Market Research] Load Auto-Matched Items',
  props<{ batchId: string }>()
);

export const loadAutoMatchedItemsSuccess = createAction(
  '[Market Research] Load Auto-Matched Items Success',
  props<{ items: MatchQueueItem[] }>()
);

export const loadAutoMatchedItemsFail = createAction(
  '[Market Research] Load Auto-Matched Items Fail',
  props<{ error: string }>()
);

// Load No-Match / Rejected Items — the resolved rows /review and /auto-matched no longer return
export const loadNoMatchItems = createAction(
  '[Market Research] Load No-Match Items',
  props<{ batchId: string }>()
);

export const loadNoMatchItemsSuccess = createAction(
  '[Market Research] Load No-Match Items Success',
  props<{ items: MatchQueueItem[] }>()
);

export const loadNoMatchItemsFail = createAction(
  '[Market Research] Load No-Match Items Fail',
  props<{ error: string }>()
);

// Load every row in the batch, any status — the only way "All" includes CONFIRMED rows
export const loadAllQueueItems = createAction(
  '[Market Research] Load All Queue Items',
  props<{ batchId: string }>()
);

export const loadAllQueueItemsSuccess = createAction(
  '[Market Research] Load All Queue Items Success',
  props<{ items: MatchQueueItem[] }>()
);

export const loadAllQueueItemsFail = createAction(
  '[Market Research] Load All Queue Items Fail',
  props<{ error: string }>()
);

// Bulk confirm auto-matched rows at or above a score threshold
export const confirmAutoMatchedAbove = createAction(
  '[Market Research] Confirm Auto-Matched Above',
  props<{ batchId: string; minScore: number }>()
);

export const confirmAutoMatchedAboveSuccess = createAction(
  '[Market Research] Confirm Auto-Matched Above Success',
  props<{ batchId: string; confirmed: number; failedNames: string[] }>()
);

export const confirmAutoMatchedAboveFail = createAction(
  '[Market Research] Confirm Auto-Matched Above Fail',
  props<{ error: string }>()
);

// Search Batches
export const searchBatches = createAction(
  '[Market Research] Search Batches',
  props<{ brandId?: string; fromDate?: string; toDate?: string }>()
);

export const searchBatchesSuccess = createAction(
  '[Market Research] Search Batches Success',
  props<{ batches: BatchSummary[] }>()
);

export const searchBatchesFail = createAction(
  '[Market Research] Search Batches Fail',
  props<{ error: string }>()
);

// Select Batch
export const selectBatch = createAction(
  '[Market Research] Select Batch',
  props<{ batch: BatchSummary }>()
);

// Review Tab
export const changeReviewTab = createAction(
  '[Market Research] Change Review Tab',
  props<{ tab: 'needs' | 'auto' }>()
);

// Confirm/Reject Match
/**
 * manualMatch carries display info for an org picked from "search the full
 * database" — one that was never part of this row's pre-computed suggestions,
 * so there's nothing already in state to show it as the top match. Omitted
 * when confirming a suggestion that's already in `row.suggestions`.
 */
export const confirmMatch = createAction(
  '[Market Research] Confirm Match',
  props<{
    queueId: number;
    bhiveId: string;
    manualMatch?: { companyName: string; companyAddress?: string; bhiveOrg?: boolean };
  }>()
);

export const confirmMatchSuccess = createAction(
  '[Market Research] Confirm Match Success',
  props<{
    queueId: number;
    bhiveId: string;
    manualMatch?: { companyName: string; companyAddress?: string; bhiveOrg?: boolean };
  }>()
);

export const confirmMatchFail = createAction(
  '[Market Research] Confirm Match Fail',
  props<{ error: string }>()
);

export const rejectMatch = createAction(
  '[Market Research] Reject Match',
  props<{ queueId: number }>()
);

export const rejectMatchSuccess = createAction(
  '[Market Research] Reject Match Success',
  props<{ queueId: number }>()
);

export const rejectMatchFail = createAction(
  '[Market Research] Reject Match Fail',
  props<{ error: string }>()
);

// Save/Import
export const saveToDatabase = createAction(
  '[Market Research] Save To Database',
  props<{ batchId: string; mode: 'MERGE' | 'REPLACE' }>()
);

export const saveToDatabaseSuccess = createAction(
  '[Market Research] Save To Database Success',
  props<{ result: ImportConnectionsResult }>()
);

export const saveToDatabaseFail = createAction(
  '[Market Research] Save To Database Fail',
  props<{ error: string }>()
);

// Download the enriched results workbook
export const downloadResults = createAction(
  '[Market Research] Download Results',
  props<{ batchId: string }>()
);

export const downloadResultsSuccess = createAction(
  '[Market Research] Download Results Success',
  props<{ blob: Blob; batchId: string }>()
);

export const downloadResultsFail = createAction(
  '[Market Research] Download Results Fail',
  props<{ error: string }>()
);
